import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LogOut } from "../../../Redux/authSlice";
import { setLoaderFalse, setLogoutLoading } from "../../../Redux/loaderSlice";
import { Button } from "primereact/button";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useGetNotificationListQuery } from "../../../rtk-query/features/usernotification/notificationRTK";
import { useSubscription } from "../../../hooks/useSubscription";
import { PRIVILEGES } from "../../../utils/privileges";
import { clearChat } from "../../../rtk-query/features/chatbot/chatbotSlice";

const RecursiveCall = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasSubscription } = useSubscription();
  const authReducer = useSelector((state) => state.authReducer);
  const unauthorizedForceLogout = useSelector((state) => state.stripeReducer.unauthorizedForceLogout);
  const [visible, setVisible] = useState(false);
  const promptedRef = useRef(false);
  const notificationAPICalledRef = useRef(false);

  const hasBannerNotificationSubscription = hasSubscription(PRIVILEGES.SHOW_BANNER_NOTIFICATION);
  const expirationTimeMs = useMemo(
    () => new Date(authReducer?.expirationTime).getTime(),
    [authReducer?.expirationTime]
  );
  const loggedInTimeMs = useMemo(
    () => new Date(authReducer?.loggedInTime).getTime(),
    [authReducer?.loggedInTime]
  );

  const { refetch } = useGetNotificationListQuery(undefined, {
    skip: !hasBannerNotificationSubscription,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      if (Number.isFinite(expirationTimeMs) && now > expirationTimeMs && !promptedRef.current) {
        setVisible(true);
        promptedRef.current = true;
      }

      if (!hasBannerNotificationSubscription || !Number.isFinite(loggedInTimeMs)) {
        return;
      }

      const elapsedMinutes = Math.floor((now - loggedInTimeMs) / (60 * 1000));

      if (elapsedMinutes > 0 && elapsedMinutes % 20 === 0) {
        if (!notificationAPICalledRef.current) {
          refetch();
          notificationAPICalledRef.current = true;
        }
      } else {
        notificationAPICalledRef.current = false;
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [expirationTimeMs, loggedInTimeMs, hasBannerNotificationSubscription, refetch]);

  useEffect(() => {
    if (unauthorizedForceLogout) {
      setVisible(true);
    }
  }, [unauthorizedForceLogout]);

  const isSessionExpired = Number.isFinite(expirationTimeMs) && expirationTimeMs < Date.now();

  const handleLogout = useCallback(async () => {
    setVisible(false);
    dispatch(clearChat());
    dispatch(setLogoutLoading(true));

    try {
      await dispatch(LogOut()).unwrap();
      dispatch(setLoaderFalse());
      navigate("/user/login");
    } catch (err) {
      dispatch(setLoaderFalse());
    }
  }, [dispatch, navigate]);

  const handleModalHide = useCallback(() => {
    if (!visible) return;
    setVisible(false);
    dispatch(setLogoutLoading(true));
    dispatch(LogOut());
  }, [dispatch, visible]);

  return (
    <>
      {/* Testing session timeout */}
      <div className="card flex justify-content-center">
        <Modal
          className="emailCampaignPagePopup"
          show={visible}
          onHide={handleModalHide}
          size="md"
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header className="px-6 pt-6 border-bottom-0">
            <h5 className="modal-title custompopup blockTitle mb-4">
              Session Timeout
            </h5>
          </Modal.Header>

          <Modal.Body className="px-6 pt-0">
            {isSessionExpired
              ? <p className="m-0 mb-10">
                Your session has expired. Please log in to continue where you left
                off.
              </p>
              : <p className="m-0 mb-10">
                Your session has ended for security reasons. This may be due to inactivity or a new login in another tab or browser. Please sign in again to continue, or use your active session in another browser.
              </p>
            }
            <Button
              label="OK"
              className="rounded"
              onClick={handleLogout}
            />
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default RecursiveCall;
